import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, FileDown, Eye, ChevronDown, ChevronUp, Calculator, Edit, X, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductionEntry({ userRole }) {
  const { selectedTenantId, user } = useAuth();
  const canEdit = user?.is_master || userRole === 'admin';
  const [apontamentos, setApontamentos] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [operationTypes, setOperationTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    periodo_inicio: '', periodo_fim: '', location_id: '',
    unidade_producao: 'Hectare', quantidade_total_produzida: '',
    observacao: '', linhas: [{ maquina_id: '', operador_id: '', tipo_operacao_id: '' }]
  });

  const headers = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'X-Tenant-Id': selectedTenantId, 'Content-Type': 'application/json' };
  }, [selectedTenantId]);

  const fetchData = useCallback(async () => {
    try {
      const h = headers();
      const [aptRes, machRes, opRes, typesRes, locRes] = await Promise.all([
        fetch(`${API_URL}/api/producao/apontamentos`, { headers: h }),
        fetch(`${API_URL}/api/machines`, { headers: h }),
        fetch(`${API_URL}/api/operators`, { headers: h }),
        fetch(`${API_URL}/api/operation-types/all`, { headers: h }),
        fetch(`${API_URL}/api/locations`, { headers: h })
      ]);
      if (aptRes.ok) setApontamentos(await aptRes.json());
      if (machRes.ok) setMachines(await machRes.json());
      if (opRes.ok) setOperators(await opRes.json());
      if (typesRes.ok) {
        const data = await typesRes.json();
        setOperationTypes(data.filter(t => t.ativo));
      }
      if (locRes.ok) setLocations(await locRes.json());
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addLine = () => setForm(f => ({ ...f, linhas: [...f.linhas, { maquina_id: '', operador_id: '', tipo_operacao_id: '' }] }));
  const removeLine = (i) => setForm(f => ({ ...f, linhas: f.linhas.filter((_, idx) => idx !== i) }));
  const updateLine = (i, field, val) => setForm(f => {
    const l = [...f.linhas]; l[i] = { ...l[i], [field]: val }; return { ...f, linhas: l };
  });

  const handleSave = async () => {
    if (!form.periodo_inicio || !form.periodo_fim) { toast.error('Período é obrigatório'); return; }
    if (!form.quantidade_total_produzida || parseFloat(form.quantidade_total_produzida) <= 0) { toast.error('Quantidade total deve ser maior que zero'); return; }
    if (!editingId && form.linhas.some(l => !l.maquina_id)) { toast.error('Selecione a máquina em todas as linhas'); return; }

    setSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/api/producao/apontamentos/${editingId}`
        : `${API_URL}/api/producao/apontamentos`;
      const method = editingId ? 'PUT' : 'POST';
      const payload = editingId
        ? { periodo_inicio: form.periodo_inicio, periodo_fim: form.periodo_fim, location_id: form.location_id, unidade_producao: form.unidade_producao, quantidade_total_produzida: parseFloat(form.quantidade_total_produzida), observacao: form.observacao }
        : { ...form, quantidade_total_produzida: parseFloat(form.quantidade_total_produzida) };
      
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      if (res.ok) {
        toast.success(editingId ? 'Apontamento atualizado' : `Apontamento criado!`);
        setShowForm(false);
        setEditingId(null);
        setForm({ periodo_inicio: '', periodo_fim: '', location_id: '', unidade_producao: 'Hectare', quantidade_total_produzida: '', observacao: '', linhas: [{ maquina_id: '', operador_id: '', tipo_operacao_id: '' }] });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao salvar');
      }
    } catch { toast.error('Erro de conexao'); }
    finally { setSaving(false); }
  };

  const handleEdit = (apt) => {
    setEditingId(apt.id);
    setForm({
      periodo_inicio: apt.periodo_inicio || '',
      periodo_fim: apt.periodo_fim || '',
      location_id: apt.location_id || '',
      unidade_producao: apt.unidade_producao || 'Hectare',
      quantidade_total_produzida: String(apt.quantidade_total_produzida || ''),
      observacao: apt.observacao || '',
      linhas: [{ maquina_id: '', operador_id: '', tipo_operacao_id: '' }]
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este apontamento de producao? Esta acao nao pode ser desfeita.')) return;
    try {
      const res = await fetch(`${API_URL}/api/producao/apontamentos/${id}`, { method: 'DELETE', headers: headers() });
      if (res.ok) { toast.success('Apontamento excluido'); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erro ao excluir'); }
    } catch { toast.error('Erro de conexao'); }
  };

  const handleRecalculate = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/producao/apontamentos/${id}/recalcular`, {
        method: 'POST', headers: headers()
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Recálculo concluído: ${result.total_horas}h em ${result.total_linhas} linhas`);
        fetchData();
        if (expandedId === id) {
          const detailRes = await fetch(`${API_URL}/api/producao/apontamentos/${id}`, { headers: headers() });
          if (detailRes.ok) setExpandedData(await detailRes.json());
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao recalcular');
      }
    } catch { toast.error('Erro de conexão'); }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); setExpandedData(null); return; }
    setExpandedId(id);
    try {
      const res = await fetch(`${API_URL}/api/producao/apontamentos/${id}`, { headers: headers() });
      if (res.ok) setExpandedData(await res.json());
    } catch { toast.error('Erro ao buscar detalhes'); }
  };

  const formatDate = (d) => { if (!d) return ''; try { const [y,m,day] = d.split('-'); return `${day}/${m}/${y}`; } catch { return d; } };

  if (loading) return <div className="p-6 text-gray-400">Carregando...</div>;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="production-entry-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Apontamento de Produção</h1>
          <p className="text-sm text-gray-500 mt-1">Registre a produção e o sistema calcula automaticamente a participação de cada máquina</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium text-sm flex items-center gap-2"
          data-testid="new-apontamento-btn">
          <Plus size={16} /> Novo Apontamento
        </button>
      </div>

      {showForm && (
        <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg p-5 mb-6" data-testid="apontamento-form">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Calculator size={18} className="text-yellow-500" /> {editingId ? 'Editar Apontamento' : 'Novo Apontamento de Producao'}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Data Início *</label>
              <input type="date" value={form.periodo_inicio} onChange={e => setForm({...form, periodo_inicio: e.target.value})}
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                data-testid="input-periodo-inicio" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Data Fim *</label>
              <input type="date" value={form.periodo_fim} onChange={e => setForm({...form, periodo_fim: e.target.value})}
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                data-testid="input-periodo-fim" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Unidade</label>
              <select value={form.unidade_producao} onChange={e => setForm({...form, unidade_producao: e.target.value})}
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                data-testid="input-unidade-producao">
                <option value="Hectare">Hectare</option>
                <option value="Alqueire">Alqueire</option>
                <option value="Hora-Máquina">Hora-Máquina</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Qtd Total Produzida *</label>
              <input type="number" step="0.01" value={form.quantidade_total_produzida} onChange={e => setForm({...form, quantidade_total_produzida: e.target.value})}
                placeholder="0.00"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                data-testid="input-qtd-total" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Local de Operação</label>
              <select value={form.location_id} onChange={e => setForm({...form, location_id: e.target.value})}
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                data-testid="input-location-id">
                <option value="">Selecione o local</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Observação</label>
              <input type="text" value={form.observacao} onChange={e => setForm({...form, observacao: e.target.value})}
                placeholder="Opcional"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                data-testid="input-observacao" />
            </div>
          </div>

          <div className={`mb-3 ${editingId ? 'hidden' : ''}`}>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm text-gray-400 font-medium">Linhas de Participação</label>
              <button onClick={addLine} className="text-xs text-yellow-500 hover:text-yellow-400 flex items-center gap-1" data-testid="add-line-btn">
                <Plus size={12} /> Adicionar Máquina
              </button>
            </div>
            <div className="space-y-2">
              {form.linhas.map((linha, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-[#0A0A0A] p-2 rounded-lg border border-gray-800">
                  <div className="col-span-4">
                    <select value={linha.maquina_id} onChange={e => updateLine(i, 'maquina_id', e.target.value)}
                      className="w-full px-2 py-2 bg-[#111] border border-gray-700 rounded text-white text-xs focus:border-yellow-500 focus:outline-none"
                      data-testid={`line-machine-${i}`}>
                      <option value="">Máquina *</option>
                      {machines.map(m => <option key={m.id} value={m.id}>{m.serial_number || ''} {m.brand} {m.model}</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <select value={linha.operador_id} onChange={e => updateLine(i, 'operador_id', e.target.value)}
                      className="w-full px-2 py-2 bg-[#111] border border-gray-700 rounded text-white text-xs focus:border-yellow-500 focus:outline-none"
                      data-testid={`line-operator-${i}`}>
                      <option value="">Operador</option>
                      {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-4">
                    <select value={linha.tipo_operacao_id} onChange={e => updateLine(i, 'tipo_operacao_id', e.target.value)}
                      className="w-full px-2 py-2 bg-[#111] border border-gray-700 rounded text-white text-xs focus:border-yellow-500 focus:outline-none"
                      data-testid={`line-optype-${i}`}>
                      <option value="">Tipo Operação</option>
                      {operationTypes.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1 text-center">
                    {form.linhas.length > 1 && (
                      <button onClick={() => removeLine(i)} className="p-1 text-red-400 hover:text-red-300" data-testid={`remove-line-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} disabled={saving}
              className="px-5 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium text-sm disabled:opacity-50"
              data-testid="save-apontamento-btn">
              {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Salvar e Calcular')}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm">Cancelar</button>
          </div>
        </div>
      )}

      {/* Lista de apontamentos */}
      <div className="space-y-3">
        {apontamentos.length === 0 && !showForm && (
          <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg p-8 text-center text-gray-500">
            Nenhum apontamento cadastrado. Clique em "Novo Apontamento" para começar.
          </div>
        )}
        {apontamentos.map(apt => (
          <div key={apt.id} className="bg-[#1A1A1A] border border-gray-800 rounded-lg overflow-hidden" data-testid={`apontamento-${apt.id}`}>
            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#111]" onClick={() => toggleExpand(apt.id)}>
              <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                <div><span className="text-gray-500 text-xs">Periodo</span><br/><span className="text-white">{formatDate(apt.periodo_inicio)} - {formatDate(apt.periodo_fim)}</span></div>
                <div><span className="text-gray-500 text-xs">Area</span><br/><span className="text-white">{apt.descricao_area || '-'}</span></div>
                <div><span className="text-gray-500 text-xs">Producao</span><br/><span className="text-yellow-500 font-semibold">{apt.quantidade_total_produzida} {apt.unidade_producao}</span></div>
                <div><span className="text-gray-500 text-xs">Horas</span><br/><span className="text-white">{apt.total_horas}h</span></div>
                <div><span className="text-gray-500 text-xs">Maquinas</span><br/><span className="text-white">{apt.total_linhas}</span></div>
              </div>
              <div className="flex items-center gap-1 ml-2">
                {canEdit && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); handleRecalculate(apt.id); }}
                      className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors" title="Recalcular Horas e Produção"
                      data-testid={`recalc-apontamento-${apt.id}`}>
                      <RefreshCw size={15} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleEdit(apt); }}
                      className="p-1.5 text-gray-400 hover:text-yellow-500 transition-colors" title="Editar"
                      data-testid={`edit-apontamento-${apt.id}`}>
                      <Edit size={15} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(apt.id); }}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors" title="Excluir"
                      data-testid={`delete-apontamento-${apt.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
                {expandedId === apt.id ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </div>
            </div>

            {expandedId === apt.id && expandedData && (
              <div className="border-t border-gray-800 p-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-800">
                      <th className="text-left py-2 px-2">Máquina</th>
                      <th className="text-left py-2 px-2">Operador</th>
                      <th className="text-left py-2 px-2">Tipo Op.</th>
                      <th className="text-center py-2 px-2">Horas</th>
                      <th className="text-center py-2 px-2">Litros</th>
                      <th className="text-center py-2 px-2">Custo Comb.</th>
                      <th className="text-center py-2 px-2">Qtd Atribuída</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expandedData.linhas?.map((l, i) => (
                      <tr key={i} className="border-b border-gray-800/50 text-white">
                        <td className="py-2 px-2">{l.maquina_nome || '-'}</td>
                        <td className="py-2 px-2">{l.operador_nome || '-'}</td>
                        <td className="py-2 px-2">{l.tipo_operacao_nome || '-'}</td>
                        <td className="text-center py-2 px-2">{l.horas_no_periodo}h</td>
                        <td className="text-center py-2 px-2">{l.litros_no_periodo}L</td>
                        <td className="text-center py-2 px-2">R$ {l.custo_combustivel_no_periodo?.toFixed(2)}</td>
                        <td className="text-center py-2 px-2 text-yellow-500 font-medium">{l.quantidade_atribuida?.toFixed(2)} {expandedData.unidade_producao}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {expandedData.observacao && <p className="text-gray-500 text-xs mt-3">Obs: {expandedData.observacao}</p>}
                <p className="text-gray-600 text-xs mt-1">Criado por {expandedData.criado_por} em {expandedData.criado_em?.split('T')[0]}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
